import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { GraphApiKeyRoleSelect } from 'src/app/account/membersPage/roleSelect/GraphApiKeyRoleSelect';
import { Code } from 'src/components/code/Code';
import { Form as CustomForm } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphApiKeyRoleOption } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { UserPermission } from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

export const APIKeysModalQuery = gql`
  query GraphSettings__APIKeysModal($graphId: ID!) {
    service(id: $graphId) {
      id
      account {
        id
        availableRoles
      }
    }
  }
`;

export const ApiKeysModal = ({
  onClose,
  graphId,
}: {
  graphId: string;
  onClose: () => void;
}) => {
  const client = useApolloClient();
  const defaultNewGraphApiKey = UserPermission.GRAPH_ADMIN;
  const [newGraphApiKeyRole, setNewGraphApiKeyRole] =
    useState<GraphApiKeyRoleOption>(defaultNewGraphApiKey);
  const [newKey, setNewKey] =
    useState<GraphQLTypes.GenerateAPIKeyMutation_service_newKey>();
  const [valueToAdd, setValueToAdd] = React.useState<string>('');

  const { data, loading, error } = useQuery<
    GraphQLTypes.GraphSettings__APIKeysModal,
    GraphQLTypes.GraphSettings__APIKeysModalVariables
  >(APIKeysModalQuery, {
    variables: {
      graphId,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  const [addKeyMutation, { loading: addKeyLoading }] = useMutation<
    GraphQLTypes.GenerateAPIKeyMutation,
    GraphQLTypes.GenerateAPIKeyMutationVariables
  >(
    gql`
      mutation GenerateAPIKeyMutation(
        $serviceId: ID!
        $name: String
        $role: UserPermission!
      ) {
        service(id: $serviceId) {
          service {
            id
          }
          newKey(keyName: $name, role: $role) {
            id
            token
            keyName
            # This fragment can be removed if/when https://github.com/mdg-private/monorepo/pull/5108 is released
            # and the field can be directly queried
            ... on GraphApiKey {
              role
            }
          }
        }
      }
    `,
    {
      onCompleted: (returnData) => {
        client.cache.evict({
          id: client.cache.identify({
            __typename: returnData.service?.service.__typename,
            id: returnData.service?.service.id,
          }),
          fieldName: 'apiKeys',
        });
        setNewKey(returnData.service?.newKey);
      },
      onError: (e) => {
        throw e;
      },
    },
  );

  // Ideally we should probably allow orgs that have access to
  // `persistedQueries` but not `userRoles` use the role popup to switch between
  // `GRAPH_ADMIN` and `PERSISTED_QUERY_PUBLISHER`. (At this time, all
  // non-legacy plans either have both or neither of those capabilities, and no
  // prod orgs have the legacy plans that only have persistedQueries.) But
  // changing `disableRolePopup` to be `!userRoles && !persistedQueries` leads
  // to a surprising popup that (a) includes CONSUMER as an option despite
  // non-Enterprise orgs not usually being able to choose CONSUMER as a graph
  // API key role and (b) does *not* include GRAPH_ADMIN as an allowed option
  // despite it being the default for non-Enterprise orgs! It's not clear
  // whether we intend to allow CONSUMER for API keys for non-Enterprise orgs
  // (it is allowed as an org membership role). And in fact there is no
  // plan-based server-side enforcement of graph API key role limits! (The
  // reason for (b) is that at the org level, free orgs get `ORG_ADMIN` but not
  // `GRAPH_ADMIN`... but there's no graph API key `GRAPH_ADMIN`.)
  //
  // The real solution here probably involves both:
  //
  // (a) Figuring out if the intention for non-Enterprise graph API keys is
  //     "GRAPH_ADMIN only" (matching the frontend behavior), "any roles
  //     allowed" (matching the backend behavior), or "GRAPH_ADMIN and CONSUMER
  //     only" (matching the org-member behavior), and updating frontend and
  //     backend to match.
  //
  // (b) Changing `Account.availableRoles` to have an argument that means "tell
  //     me about available graphs API key roles instead of available org
  //     membership roles" (or adding a new field).
  //
  // For the time being we're just going to be OK assuming userRoles ==
  // persistedQueries and hiding the popup for !userRoles.
  //
  // See https://apollograph.slack.com/archives/C01UYT99JVA/p1692378461651249
  // which hopefully can turn into a ticket in an appropriate place.
  const disableRolePopup = !currentPlan?.currentPlan?.userRoles;

  return (
    <PermissionGuard
      graphId={graphId}
      graphPermissions="canQueryTokens"
      fallback={null}
    >
      <div className="-mx-6 text-sm text-secondary">
        <Modal isOpen onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <CustomForm
              onSubmit={(event) => {
                const name = new FormData(event.currentTarget).get('name');
                if (typeof name !== 'string') {
                  throw new Error('Could not get new key name');
                }
                addKeyMutation({
                  variables: {
                    serviceId: graphId,
                    name,
                    role: newGraphApiKeyRole,
                  },
                });
              }}
            >
              <ModalHeader>
                <ModalTitle>New API Key</ModalTitle>
              </ModalHeader>
              <ModalBody>
                {loading || currentPlan.loading ? (
                  <Loading size="md" />
                ) : error || currentPlan.error ? (
                  <ErrorMessage error={error ?? currentPlan.error} />
                ) : newKey ? (
                  <>
                    <Alert status="info">
                      Make sure to copy your API key now. You won't be able to
                      see it again.
                    </Alert>
                    <Code
                      showCopyButton
                      code={newKey.token}
                      className="flex justify-between rounded p-3"
                    />
                  </>
                ) : (
                  <div className="flex flex-row gap-4 overflow-x-hidden">
                    <FormControl className="basis-3/4">
                      <FormLabel>Key Name</FormLabel>
                      <Input
                        type="text"
                        autoFocus
                        value={valueToAdd}
                        onChange={(e) => setValueToAdd(e.target.value)}
                        name="name"
                      />
                    </FormControl>
                    <FormControl className="basis-1/4">
                      <FormLabel>
                        Key Role{' '}
                        <Tooltip
                          interactive
                          label={
                            disableRolePopup ? (
                              <p>
                                This is an Enterprise feature. Please{' '}
                                <ContactSupportLink>
                                  get in touch with us
                                </ContactSupportLink>{' '}
                                if you need to be able to adjust the permissions
                                of your graph's API keys.
                              </p>
                            ) : (
                              <p>
                                You can read about the different roles an API
                                key can have in the{' '}
                                <Link
                                  to={DocsRouter.path('GraphApiKeyRoles')}
                                  isExternal
                                >
                                  Studio documentation.
                                </Link>
                              </p>
                            )
                          }
                        >
                          <IconInfo className="ml-2" tabIndex={0} />
                        </Tooltip>
                      </FormLabel>
                      <GraphApiKeyRoleSelect
                        target="graph"
                        disabled={disableRolePopup}
                        availableRoles={
                          data?.service?.account?.availableRoles || []
                        }
                        userRole={newGraphApiKeyRole}
                        setUserRole={setNewGraphApiKeyRole}
                        placement="bottom-end"
                        matchTriggerWidth={false}
                        className="h-[40px]"
                      />
                    </FormControl>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                {newKey ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      onClose();
                    }}
                  >
                    Close
                  </Button>
                ) : (
                  <ButtonGroup>
                    <Button variant="secondary" type="button" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      isDisabled={valueToAdd?.length === 0}
                      isLoading={addKeyLoading}
                      loadingText="Create"
                    >
                      Create
                    </Button>
                  </ButtonGroup>
                )}
              </ModalFooter>
            </CustomForm>
          </ModalContent>
        </Modal>
      </div>
    </PermissionGuard>
  );
};
